import { useState, useCallback } from 'react';

interface ConfirmationConfig {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'warning' | 'info';
}

interface UseConfirmationResult {
  isOpen: boolean;
  config: ConfirmationConfig | null;
  itemId: number | null;
  selectedItems: number[];
  openConfirmation: (config: ConfirmationConfig, id?: number) => void;
  closeConfirmation: () => void;
  handleMultipleConfirmation: (config: ConfirmationConfig) => void;
  setSelectedItems: (items: number[]) => void;
  handleConfirm: () => void;
}

export const useConfirmation = (onConfirm: (id?: number) => void): UseConfirmationResult => {
  const [isOpen, setIsOpen] = useState(false);
  const [config, setConfig] = useState<ConfirmationConfig | null>(null);
  const [itemId, setItemId] = useState<number | null>(null);
  const [selectedItems, setSelectedItems] = useState<number[]>([]);

  const openConfirmation = useCallback((config: ConfirmationConfig, id?: number) => {
    setConfig(config);
    if (id) {
      setItemId(id);
    } else {
      setItemId(null);
    }
    setIsOpen(true);
  }, []);

  const closeConfirmation = useCallback(() => {
    setIsOpen(false);
    setConfig(null);
    setItemId(null);
  }, []);

  const handleMultipleConfirmation = useCallback((config: ConfirmationConfig) => {
    setConfig(config);
    setItemId(null);
    setIsOpen(true);
  }, []);

  const handleConfirm = useCallback(() => {
    if (itemId) {
      onConfirm(itemId);
    } else if (selectedItems.length) {
      onConfirm();
    } else {
      onConfirm(undefined);
    }
    closeConfirmation();
  }, [itemId, selectedItems, onConfirm, closeConfirmation]);

  return {
    isOpen,
    config,
    itemId,
    selectedItems,
    openConfirmation,
    closeConfirmation,
    handleMultipleConfirmation,
    setSelectedItems,
    handleConfirm,
  };
};
